/**
 * BoardBackgroundSelector Component
 *
 * Allows users to browse previously uploaded backgrounds, upload new ones,
 * change display mode, and remove the board background image.
 */

import React, { useRef, useState, useEffect } from 'react';
import {
  useUploadBoardBackground,
  BackgroundDisplayMode,
} from '@/hooks/useUploadBoardBackground';
import { BoardPreferences } from '@/types/preferences.types';
import { useThemeColors } from '@/styles/colors/useThemeColors';
import { spacing, typography, shadows, radius } from '@/styles/design-tokens';
import { IconName } from '@/icons';
import { AsyncButton } from '@/components/ui';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { useBoardBackgroundsQuery } from '@/hooks/queries';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { Action } from '@/business/types/action.types';
import type {
  ListBoardBackgroundsBackgroundsItem,
  UpdateBoardBackgroundInput,
  UpdateBoardBackgroundOutput,
} from '@/types/generated';

/**
 * Extract image dimensions from a File before upload
 */
async function getFileDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image dimensions'));
    };
    img.src = url;
  });
}

/**
 * Strip query params from a URL so presigned S3 URLs can be compared
 * against permanent S3 URLs.
 */
function getBaseUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.origin + u.pathname;
  } catch {
    return url;
  }
}

/**
 * Pick the best display mode based on image size vs board viewport.
 * Images too small to cover the board without significant upscaling
 * default to 'tile' to avoid pixelation.
 */
function getSmartDisplayMode(dims: {
  width: number;
  height: number;
}): BackgroundDisplayMode {
  const boardWidth = window.innerWidth - 280;
  const boardHeight = window.innerHeight - 64;
  if (dims.width >= boardWidth * 0.8 && dims.height >= boardHeight * 0.8) {
    return BackgroundDisplayMode.COVER;
  }
  return BackgroundDisplayMode.TILE;
}

/**
 * Component props
 */
export interface BoardBackgroundSelectorProps {
  boardId: string;
  currentBackground?: BoardPreferences['background'];
  onBackgroundChange?: (background?: BoardPreferences['background']) => void;
}

/**
 * Display mode options
 */
const DISPLAY_MODE_OPTIONS: Array<{
  value: BackgroundDisplayMode;
  label: string;
  description: string;
}> = [
  {
    value: BackgroundDisplayMode.COVER,
    label: 'Cover',
    description: 'Fills board, may crop edges (recommended)',
  },
  {
    value: BackgroundDisplayMode.CONTAIN,
    label: 'Contain',
    description: 'Fits entire image, may show gaps',
  },
  {
    value: BackgroundDisplayMode.TILE,
    label: 'Tile',
    description: 'Repeats pattern (best for small images)',
  },
  {
    value: BackgroundDisplayMode.CENTER,
    label: 'Center',
    description: 'Original size, centered (best for large images)',
  },
  {
    value: BackgroundDisplayMode.STRETCH,
    label: 'Stretch',
    description: 'Stretches to fill (may distort proportions)',
  },
];

/**
 * Board Background Selector Component
 */
export function BoardBackgroundSelector({
  boardId,
  currentBackground,
  onBackgroundChange,
}: BoardBackgroundSelectorProps): JSX.Element {
  const colors = useThemeColors();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedDisplayMode, setSelectedDisplayMode] =
    useState<BackgroundDisplayMode>(
      currentBackground?.display_mode || BackgroundDisplayMode.COVER
    );
  const [selectedBgKey, setSelectedBgKey] = useState<string | null>(null);
  const [isSelectingBackground, setIsSelectingBackground] = useState(false);

  const {
    isUploading,
    uploadProgress,
    uploadMessage,
    uploadBackground,
    removeBackground,
  } = useUploadBoardBackground();

  const { data: backgroundsData, isLoading: isLoadingBackgrounds } =
    useBoardBackgroundsQuery(boardId);
  const backgrounds = backgroundsData?.backgrounds ?? [];

  const { execute: updateBoardBackground } = useOrchestrator<
    UpdateBoardBackgroundInput,
    UpdateBoardBackgroundOutput
  >(Action.UPDATE_BOARD_BACKGROUND);

  /**
   * Sync selectedDisplayMode when currentBackground changes
   */
  useEffect(() => {
    if (currentBackground?.display_mode) {
      setSelectedDisplayMode(currentBackground.display_mode);
    }
  }, [currentBackground?.display_mode]);

  /**
   * Identify which gallery item matches the current background.
   * currentBackground.absolute_url may be a presigned URL, so strip query
   * params before comparing against the permanent bg.url.
   */
  useEffect(() => {
    if (!currentBackground?.absolute_url || backgrounds.length === 0) return;
    const base = getBaseUrl(currentBackground.absolute_url);
    const match = backgrounds.find(bg => bg.url === base);
    setSelectedBgKey(match?.key ?? null);
  }, [currentBackground?.absolute_url, backgrounds]);

  /**
   * Select an existing background from the gallery.
   * Sets selectedBgKey immediately for visual feedback, then saves to backend
   * and triggers a refetch so the parent receives the presigned URL
   * (required for display on private S3 buckets).
   */
  const handleSelectExisting = async (
    bg: ListBoardBackgroundsBackgroundsItem
  ) => {
    setSelectedBgKey(bg.key); // instant visual feedback
    setIsSelectingBackground(true);
    try {
      await updateBoardBackground({
        board_id: boardId,
        background_url: bg.url,
        display_mode: selectedDisplayMode,
      });
      // Signal the modal to refetch preferences — returns presigned URL for display
      onBackgroundChange?.();
    } finally {
      setIsSelectingBackground(false);
    }
  };

  /**
   * Handle file selection (new upload)
   */
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    let modeToUse = selectedDisplayMode;
    try {
      const dims = await getFileDimensions(file);
      const smartMode = getSmartDisplayMode(dims);
      setSelectedDisplayMode(smartMode);
      modeToUse = smartMode;
    } catch {
      // fall back to current mode
    }

    const result = await uploadBackground(file, boardId, modeToUse);

    if (result?.background) {
      onBackgroundChange?.();
      queryClient.invalidateQueries({
        queryKey: queryKeys.boardBackgrounds.forBoard(boardId),
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle display mode change (optimistic update)
   */
  const handleDisplayModeChange = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newDisplayMode = event.target.value as BackgroundDisplayMode;
    setSelectedDisplayMode(newDisplayMode);

    if (currentBackground?.absolute_url) {
      const updatedBackground: BoardPreferences['background'] = {
        ...currentBackground,
        display_mode: newDisplayMode,
      };
      onBackgroundChange?.(updatedBackground);
    }
  };

  /**
   * Handle remove background
   */
  const handleRemove = async () => {
    const result = await removeBackground(boardId);
    if (result?.success) {
      onBackgroundChange?.();
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      style={{
        padding: spacing.md,
        backgroundColor: colors.offWhite,
        borderRadius: radius.md,
        border: `1px solid ${colors.border.default}`,
      }}
    >
      {/* Background gallery */}
      <div style={{ marginBottom: spacing.md }}>
        <p
          style={{
            fontSize: typography.sizes.sm,
            fontWeight: 500,
            color: colors.text.primary,
            marginBottom: spacing.xs,
          }}
        >
          Backgrounds
        </p>
        <div
          style={{
            height: 'calc(88px * var(--sizing-multiplier, 1))',
            overflowX: 'auto',
            overflowY: 'hidden',
            borderRadius: radius.sm,
            border: `1px solid ${colors.border.default}`,
            backgroundColor: colors.background,
            padding: spacing.xs,
          }}
        >
          {isLoadingBackgrounds ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <span
                style={{
                  fontSize: typography.sizes.sm,
                  color: colors.text.secondary,
                }}
              >
                Loading...
              </span>
            </div>
          ) : backgrounds.length === 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <span
                style={{
                  fontSize: typography.sizes.sm,
                  color: colors.text.secondary,
                }}
              >
                No backgrounds yet. Upload one below.
              </span>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: spacing.xs,
                alignItems: 'center',
                height: '100%',
              }}
            >
              {backgrounds.map(bg => {
                const isActive = bg.key === selectedBgKey;
                const isBusy = isUploading || isSelectingBackground;
                return (
                  <button
                    key={bg.key}
                    onClick={() => handleSelectExisting(bg)}
                    disabled={isBusy}
                    style={{
                      flexShrink: 0,
                      width: 'calc(72px * var(--sizing-multiplier, 1))',
                      height: 'calc(72px * var(--sizing-multiplier, 1))',
                      borderRadius: radius.sm,
                      border: isActive
                        ? `2px solid ${colors.primary}`
                        : `1px solid ${colors.border.default}`,
                      backgroundColor: colors.background,
                      transform: isActive ? 'scale(1.04)' : 'scale(1)',
                      boxShadow: isActive ? shadows.interactive : 'none',
                      overflow: 'hidden',
                      cursor: isBusy ? 'not-allowed' : 'pointer',
                      opacity: isSelectingBackground && !isActive ? 0.5 : 1,
                      padding: 0,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <img
                      src={bg.url}
                      alt="Background"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Display mode selector */}
      <div style={{ marginBottom: spacing.sm }}>
        <label
          htmlFor="display-mode"
          style={{
            display: 'block',
            fontSize: typography.sizes.sm,
            fontWeight: 500,
            color: colors.text.primary,
            marginBottom: spacing.xs,
          }}
        >
          Display Mode
        </label>
        <select
          id="display-mode"
          value={selectedDisplayMode}
          onChange={handleDisplayModeChange}
          disabled={isUploading}
          style={{
            width: '100%',
            padding: spacing.sm,
            fontSize: typography.sizes.md,
            color: colors.text.primary,
            backgroundColor: colors.background,
            border: `1px solid ${colors.border.default}`,
            borderRadius: radius.sm,
            cursor: isUploading ? 'not-allowed' : 'pointer',
          }}
        >
          {DISPLAY_MODE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label} - {option.description}
            </option>
          ))}
        </select>
      </div>

      {/* Upload progress */}
      {isUploading && (
        <div style={{ marginBottom: spacing.sm }}>
          <div
            style={{
              width: '100%',
              height: 'calc(4px * var(--sizing-multiplier, 1))',
              backgroundColor: colors.background,
              borderRadius: radius.sm,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${uploadProgress}%`,
                height: '100%',
                backgroundColor: colors.primary,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          {uploadMessage && (
            <p
              style={{
                fontSize: typography.sizes.sm,
                color: colors.text.secondary,
                marginTop: spacing.xs,
              }}
            >
              {uploadMessage}
            </p>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        <AsyncButton
          onClick={async () => handleUploadClick()}
          disabled={isUploading}
          size="sm"
          icon={IconName.PLUS}
        >
          Upload New
        </AsyncButton>

        {currentBackground && (
          <AsyncButton
            onClick={handleRemove}
            disabled={isUploading}
            variant="outline"
            size="sm"
            icon={IconName.TRASH}
            className="text-destructive hover:bg-destructive/10 hover:border-destructive"
          >
            Remove
          </AsyncButton>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <p
        style={{
          fontSize: typography.sizes.sm,
          color: colors.text.secondary,
          marginTop: spacing.xs,
          marginBottom: 0,
        }}
      >
        Accepted formats: JPEG, PNG, WebP, GIF. Maximum size: 5MB.
      </p>
    </div>
  );
}
